import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Navigate, Outlet, useLocation } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { trackPageView } from "./lib/analytics";
import { Toaster } from "sonner";
import Layout from "./components/layout/Layout";

// Public/prerendered routes are imported eagerly so hydrateRoot can
// match the SSR-generated HTML without triggering a Suspense fallback
// (which would discard the prerendered markup).
import Home from "./pages/Home";
import NotFound from "./pages/NotFound";
import About from "./pages/About";
import Contact from "./pages/Contact";
import FAQ from "./pages/FAQ";
import Software from "./pages/Software";
import Products from "./pages/Products";
import Auctions from "./pages/Auctions";
import Login from "./pages/Login";
import Register from "./pages/Register";
import DrugNameLanding from "./pages/DrugNameLanding";
import TherapeuticClassLanding from "./pages/TherapeuticClassLanding";
import NdcLookup from "./pages/NdcLookup";
import DscsaResources from "./pages/DscsaResources";
import DscsaLandingPage from "./pages/DscsaLandingPage";
import Insights from "./pages/Insights";
import Press from "./pages/Press";
import EditorialPolicy from "./pages/EditorialPolicy";
import InsightAuthor from "./pages/InsightAuthor";

const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const SeoLanding = lazy(() => import("./pages/SeoLanding"));
const Cart = lazy(() => import("./pages/Cart"));
const Checkout = lazy(() => import("./pages/Checkout"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const AuctionDetail = lazy(() => import("./pages/AuctionDetail"));
const Favorites = lazy(() => import("./pages/Favorites"));
const CustomerInvoices = lazy(() => import("./pages/customer/Invoices"));
const EpcisInbox = lazy(() => import("./pages/customer/EpcisInbox"));
const AdminDashboard = lazy(() => import("./pages/admin/Dashboard"));
const AdminOrders = lazy(() => import("./pages/admin/Orders"));
const AdminCustomers = lazy(() => import("./pages/admin/Customers"));
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const AdminAuctions = lazy(() => import("./pages/admin/Auctions"));
const AdminCustomerPricing = lazy(() => import("./pages/admin/CustomerPricing"));
const AdminNetSuite = lazy(() => import("./pages/admin/NetSuite"));
const AdminPricingDashboard = lazy(() => import("./pages/admin/PricingDashboard"));
const AdminBulkUpload = lazy(() => import("./pages/admin/BulkUpload"));
const AdminLeads = lazy(() => import("./pages/admin/Leads"));
const AdminCarts = lazy(() => import("./pages/admin/Carts"));
const AdminAdvertising = lazy(() => import("./pages/admin/Advertising"));
const AdminLandingPages = lazy(() => import("./pages/admin/LandingPagesWorkspace"));
const AdminContent = lazy(() => import("./pages/admin/Content"));
const ExpirationDashboard = lazy(() => import("./pages/warehouse/Expiration"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentCancel = lazy(() => import("./pages/PaymentCancel"));
const Account = lazy(() => import("./pages/Account"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

import { AuthProvider, useAuth } from "./hooks/useAuth";
import { CartProvider } from "./hooks/useCart";
import { OrderOnBehalfProvider } from "./hooks/useOrderOnBehalf";
import { UnreadContactCountProvider } from "./hooks/useUnreadContactCount";
import ErrorBoundary from "./components/ErrorBoundary";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      retry: 1,
    },
  },
});

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);
  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AdminGuard() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-gray-50" />;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;
  return <Outlet />;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
    <AuthProvider>
      <UnreadContactCountProvider>
      <OrderOnBehalfProvider>
      <CartProvider>
      <Toaster position="bottom-right" richColors />
      <ScrollToTop />
      <PageViewTracker />
      <Suspense fallback={<div className="min-h-screen" />}>
      <Routes>
        {/* Standalone landing pages — use their own layout, not the site Layout */}
        <Route path="/lp/dscsa-compliance" element={<DscsaLandingPage />} />

        <Route element={<Layout />}>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/software" element={<Software />} />
          <Route path="/dscsa" element={<Navigate to="/resources/dscsa-compliance" replace />} />
          <Route path="/drugs" element={<DrugNameLanding />} />
          <Route path="/drugs/:slug" element={<DrugNameLanding />} />
          <Route path="/therapeutic-classes" element={<TherapeuticClassLanding />} />
          <Route path="/therapeutic-classes/:slug" element={<TherapeuticClassLanding />} />
          <Route path="/ndc" element={<NdcLookup />} />
          <Route path="/ndc/:ndc" element={<NdcLookup />} />
          <Route path="/resources" element={<Navigate to="/resources/dscsa-compliance" replace />} />
          <Route path="/resources/dscsa-compliance" element={<DscsaResources />} />
          <Route path="/resources/dscsa-compliance/:subpage" element={<DscsaResources />} />
          <Route path="/insights" element={<Insights />} />
          <Route path="/insights/guides" element={<Insights />} />
          <Route path="/insights/dscsa-compliance" element={<Insights />} />
          <Route path="/insights/pharmacy-operations" element={<Insights />} />
          <Route path="/insights/drug-supply" element={<Insights />} />
          <Route path="/insights/company-news" element={<Insights />} />
          <Route path="/insights/:slug" element={<Insights />} />
          <Route path="/authors/:slug" element={<InsightAuthor />} />
          <Route path="/press" element={<Press />} />
          <Route path="/editorial-policy" element={<EditorialPolicy />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/:id" element={<ProductDetail />} />
          <Route path="/categories/:slug" element={<SeoLanding mode="category" />} />
          <Route path="/manufacturers/:slug" element={<SeoLanding mode="manufacturer" />} />
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/register" element={<Register />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation/:id" element={<OrderConfirmation />} />
          <Route path="/payment-success" element={<PaymentSuccess />} />
          <Route path="/payment-cancel" element={<PaymentCancel />} />
          <Route path="/account" element={<Account />} />
          <Route path="/auctions" element={<Auctions />} />
          <Route path="/auctions/:id" element={<AuctionDetail />} />
          <Route path="/favorites" element={<Favorites />} />
          <Route path="/my-invoices" element={<CustomerInvoices />} />
          <Route path="/my-invoices/:id" element={<Navigate to="/my-invoices" replace />} />
          <Route path="/epcis" element={<EpcisInbox />} />

          <Route element={<AdminGuard />}>
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/customers" element={<AdminCustomers />} />
            <Route path="/admin/products" element={<AdminProducts />} />
            <Route path="/admin/auctions" element={<AdminAuctions />} />
            <Route path="/admin/customer-pricing" element={<AdminCustomerPricing />} />
            <Route path="/admin/netsuite" element={<AdminNetSuite />} />
            <Route path="/admin/pricing" element={<AdminPricingDashboard />} />
            <Route path="/admin/bulk-upload" element={<AdminBulkUpload />} />
            <Route path="/admin/leads" element={<AdminLeads />} />
            <Route path="/admin/carts" element={<AdminCarts />} />
            <Route path="/admin/advertising" element={<AdminAdvertising />} />
             <Route path="/admin/landing-pages" element={<AdminLandingPages />} />
             <Route path="/admin/content" element={<AdminContent />} />
          </Route>

          <Route path="/warehouse/expiration" element={<ExpirationDashboard />} />
          <Route path="/warehouse" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/orders" element={<Navigate to="/admin/orders" replace />} />
          <Route path="/warehouse/inventory" element={<Navigate to="/admin/products" replace />} />
          <Route path="/warehouse/locations" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/cycle-counts" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/quarantine" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/recall-trace" element={<Navigate to="/admin" replace />} />
          <Route path="/warehouse/receiving" element={<Navigate to="/admin" replace />} />

          <Route path="/accountant/*" element={<Navigate to="/admin" replace />} />
          <Route path="/purchasing" element={<Navigate to="/admin" replace />} />
          <Route path="/sales/*" element={<Navigate to="/admin" replace />} />

          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
      </Suspense>
      </CartProvider>
      </OrderOnBehalfProvider>
      </UnreadContactCountProvider>
    </AuthProvider>
    </ErrorBoundary>
    </QueryClientProvider>
  );
}
